/**
 * Typed thin-fetch client for the animachord REST API.
 *
 * - Uses the browser-native `fetch`. No axios.
 * - Reads its base URL from `import.meta.env.VITE_ANIMACHORD_API_BASE`,
 *   falling back to `/` so the console can ship behind a reverse proxy.
 * - Authorization is opt-in: callers wire a token-getter via
 *   `apiClient.setTokenProvider(...)`. Auth0 SDK integration lands in a
 *   later wave; this client only knows how to *use* a token.
 * - Idempotency-Key is opt-in via `apiClient.withIdempotencyKey(...)`,
 *   which returns a fluent client variant. ULID auto-generation is
 *   provided when the caller passes no key.
 * - Every non-2xx response throws `ApiError` with the parsed envelope.
 * - The `paginated` helper iterates cursor-paginated endpoints lazily.
 *
 * Implementation note: this file is the only place in the console that
 * touches `fetch` directly; component code goes through `apiClient` so
 * the auth/idempotency story can evolve in one place.
 */

import { ApiError, type ApiErrorEnvelope } from "./errors";
import type { CursorPage } from "./types";

// ============================================================================
// Vite env type augmentation (kept here so we don't rely on a .d.ts file —
// the project's .gitignore excludes src/**/*.d.ts to keep stray build
// artefacts out of source control).
// ============================================================================

declare global {
  interface ImportMetaEnv {
    readonly VITE_ANIMACHORD_API_BASE?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// ============================================================================
// Configuration
// ============================================================================

/** Async or sync token getter. Returning `null` skips the Authorization header. */
export type TokenProvider = () => Promise<string | null> | string | null;

export interface ApiClientConfig {
  /** Defaults to `import.meta.env.VITE_ANIMACHORD_API_BASE` or `/`. */
  readonly baseUrl?: string;
  /** Optional Bearer-token provider. */
  readonly tokenProvider?: TokenProvider;
  /** Override `globalThis.fetch` for testing. */
  readonly fetchImpl?: typeof fetch;
}

interface RequestOptions {
  /** URL search params; arrays append repeated keys. */
  readonly query?: Record<
    string,
    string | number | boolean | readonly string[] | undefined
  >;
  /** Extra headers (Authorization + Idempotency-Key + Content-Type are managed). */
  readonly headers?: Record<string, string>;
  /** AbortSignal forwarded to fetch. */
  readonly signal?: AbortSignal;
}

interface BodyRequestOptions extends RequestOptions {
  /** Will be JSON-serialised. Pass `undefined` to skip the body. */
  readonly body?: unknown;
}

// ============================================================================
// ULID generation (Crockford base32, monotonic-ish — good enough for keys)
// ============================================================================

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Generate a 26-char ULID. Not cryptographically perfect — uses
 * `crypto.getRandomValues` for the random tail and `Date.now()` for the
 * timestamp. Good enough for `Idempotency-Key` (server is the source of
 * truth for IDs that need ordering guarantees).
 */
export function newUlid(): string {
  const time = Date.now();
  const timeChars: string[] = [];
  let t = time;
  for (let i = 0; i < 10; i++) {
    timeChars.unshift(CROCKFORD[t % 32] ?? "0");
    t = Math.floor(t / 32);
  }
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  const randomChars: string[] = [];
  for (let i = 0; i < 16; i++) {
    randomChars.push(CROCKFORD[(randomBytes[i] ?? 0) % 32] ?? "0");
  }
  return timeChars.join("") + randomChars.join("");
}

// ============================================================================
// Internal request mechanics
// ============================================================================

interface InternalState {
  baseUrl: string;
  tokenProvider: TokenProvider | undefined;
  fetchImpl: typeof fetch;
  /** Last X-Request-Id seen, for debug surfaces. */
  lastRequestId: string | null;
  /** Per-call Idempotency-Key, set by `withIdempotencyKey`. */
  idempotencyKey: string | null;
}

function readDefaultBaseUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env["VITE_ANIMACHORD_API_BASE"] ?? "/";
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions["query"]): string {
  // Allow callers to pass either "/v1/scores" or "v1/scores".
  const normalisedPath = path.startsWith("/") ? path : `/${path}`;
  const normalisedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  let url = `${normalisedBase}${normalisedPath}`;
  if (query !== undefined) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        for (const v of value) params.append(key, v);
      } else {
        params.append(key, String(value));
      }
    }
    const qs = params.toString();
    if (qs.length > 0) url += `?${qs}`;
  }
  return url;
}

async function resolveAuthHeader(provider: TokenProvider | undefined): Promise<string | null> {
  if (provider === undefined) return null;
  const token = await provider();
  return token === null ? null : `Bearer ${token}`;
}

function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { error?: unknown }).error === "string" &&
    typeof (value as { message?: unknown }).message === "string" &&
    typeof (value as { request_id?: unknown }).request_id === "string"
  );
}

async function parseJsonSafely(response: Response): Promise<unknown> {
  // 204 No Content responses have no body.
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function executeRequest<T>(
  state: InternalState,
  method: string,
  path: string,
  opts: BodyRequestOptions,
): Promise<T> {
  const url = buildUrl(state.baseUrl, path, opts.query);
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const auth = await resolveAuthHeader(state.tokenProvider);
  if (auth !== null) headers["Authorization"] = auth;
  if (state.idempotencyKey !== null) {
    headers["Idempotency-Key"] = state.idempotencyKey;
  }

  const init: RequestInit = {
    method,
    headers,
    ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }

  const response = await state.fetchImpl(url, init);
  state.lastRequestId = response.headers.get("X-Request-Id");

  const parsed = await parseJsonSafely(response);

  if (!response.ok) {
    if (isApiErrorEnvelope(parsed)) {
      throw new ApiError({
        code: parsed.error,
        status: response.status,
        requestId: parsed.request_id,
        message: parsed.message,
        ...(parsed.details !== undefined ? { details: parsed.details } : {}),
        ...(parsed.retry_after_seconds !== undefined
          ? { retryAfterSeconds: parsed.retry_after_seconds }
          : {}),
      });
    }
    // Non-envelope error body — synthesise a minimal error.
    throw new ApiError({
      code: "internal_error",
      status: response.status,
      requestId: state.lastRequestId ?? "unknown",
      message:
        typeof parsed === "string" && parsed.length > 0
          ? parsed
          : `HTTP ${response.status}`,
    });
  }

  return parsed as T;
}

// ============================================================================
// Public client
// ============================================================================

/**
 * Public surface of the REST client. All methods return parsed JSON on
 * 2xx; throw `ApiError` on everything else.
 */
export interface ApiClient {
  readonly baseUrl: string;
  readonly lastRequestId: string | null;
  setTokenProvider(provider: TokenProvider | undefined): void;
  /**
   * Returns a fluent variant whose next request includes the supplied
   * `Idempotency-Key`. If `key` is omitted, a fresh ULID is generated.
   * The variant shares the same auth/baseUrl state.
   */
  withIdempotencyKey(key?: string): ApiClient;

  get<T>(path: string, opts?: RequestOptions): Promise<T>;
  post<T>(path: string, opts?: BodyRequestOptions): Promise<T>;
  patch<T>(path: string, opts?: BodyRequestOptions): Promise<T>;
  delete<T>(path: string, opts?: RequestOptions): Promise<T>;

  /**
   * Iterate a cursor-paginated endpoint as an async stream of pages.
   * Stops when the server reports `has_more: false` or `next_cursor: null`.
   */
  paginated<T>(
    path: string,
    opts?: RequestOptions & { readonly limit?: number },
  ): AsyncIterableIterator<CursorPage<T>>;
}

class ApiClientImpl implements ApiClient {
  private readonly state: InternalState;

  constructor(state: InternalState) {
    this.state = state;
  }

  get baseUrl(): string {
    return this.state.baseUrl;
  }

  get lastRequestId(): string | null {
    return this.state.lastRequestId;
  }

  setTokenProvider(provider: TokenProvider | undefined): void {
    this.state.tokenProvider = provider;
  }

  withIdempotencyKey(key?: string): ApiClient {
    // Share state for auth + baseUrl, but isolate the idempotency key so
    // concurrent variants don't bleed headers.
    const variantState: InternalState = {
      ...this.state,
      idempotencyKey: key ?? newUlid(),
    };
    // Note: lastRequestId reads through to a fresh slot on the variant.
    variantState.lastRequestId = this.state.lastRequestId;
    return new ApiClientImpl(variantState);
  }

  async get<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return executeRequest<T>(this.state, "GET", path, opts);
  }

  async post<T>(path: string, opts: BodyRequestOptions = {}): Promise<T> {
    return executeRequest<T>(this.state, "POST", path, opts);
  }

  async patch<T>(path: string, opts: BodyRequestOptions = {}): Promise<T> {
    return executeRequest<T>(this.state, "PATCH", path, opts);
  }

  async delete<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return executeRequest<T>(this.state, "DELETE", path, opts);
  }

  async *paginated<T>(
    path: string,
    opts: RequestOptions & { readonly limit?: number } = {},
  ): AsyncIterableIterator<CursorPage<T>> {
    let cursor: string | null = null;
    while (true) {
      const query: Record<string, string | number | boolean> = {
        ...(opts.query as Record<string, string | number | boolean> | undefined),
      };
      if (opts.limit !== undefined) query["limit"] = opts.limit;
      if (cursor !== null) query["cursor"] = cursor;

      const page = await executeRequest<CursorPage<T>>(this.state, "GET", path, {
        ...opts,
        query,
      });
      yield page;
      if (!page.has_more || page.next_cursor === null) return;
      cursor = page.next_cursor;
    }
  }
}

/** Build a fresh `ApiClient`. Most callers should use `apiClient` instead. */
export function createApiClient(config: ApiClientConfig = {}): ApiClient {
  const state: InternalState = {
    baseUrl: config.baseUrl ?? readDefaultBaseUrl(),
    tokenProvider: config.tokenProvider,
    fetchImpl: config.fetchImpl ?? globalThis.fetch.bind(globalThis),
    lastRequestId: null,
    idempotencyKey: null,
  };
  return new ApiClientImpl(state);
}

/**
 * The shared client used by the rest of the console. Component code
 * imports this — direct `fetch` calls are forbidden by ESLint config.
 */
export const apiClient: ApiClient = createApiClient();
